import axios from 'axios';
import ApiConfig from './ApiConfig';
import { getAdminToken, clearStoredLogin, setLoginNotice, setCompanySuspended } from '../utils/session';

const _baseDevURL = ApiConfig.serverDevURL + ApiConfig.basePath;
const _baseProURL = ApiConfig.serverProURL + ApiConfig.basePath;

export const baseURL = process.env.NODE_ENV === 'production' ? _baseProURL : _baseDevURL;
export const baseImgURL = process.env.NODE_ENV === 'production' ? ApiConfig.serverProURL+'/storage/' : ApiConfig.serverDevURL+'/storage/';

export const requestMethod = {
    GET:    'GET',
    POST:   'POST',
    PUT:    'PUT',
    DELETE: 'DELETE',
    FILE:   'FILE'
};

const api = axios.create({
    baseURL,
    headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
    },
    timeout: ApiConfig.timeOut,
});

// Public calls that must never carry the admin token (login, password reset, email verify)
const NO_TOKEN_ENDPOINTS = /^\/?(auth\/login(\/|$)|auth\/email\/|password\/(sendtoken|find|resetpassword)(\/|$))/;

// Calls whose own code 401 is not a session problem: a wrong password on auth/login,
// the password routes, and logout itself (an already-invalid token is fine there)
const NO_EXPIRY_HANDLING = /^\/?(auth\/login(\/|$)|password\/|auth\/adminLogout$)/;

function withAdminToken(endpoint) {
    const token = getAdminToken();
    if (!token || NO_TOKEN_ENDPOINTS.test(endpoint)) {
        return {};
    }
    return { headers: { 'Authorization': 'Bearer ' + token } };
}

let redirectingToLogin = false;

const SUSPENDED_NOTICE = 'Your company account is currently suspended, try again later';

// notice: the message for the login page; by default the usual "session expired" one
function handleSessionExpired(config, notice) {
    if (redirectingToLogin || !config || NO_EXPIRY_HANDLING.test(config.url || '')) {
        return;
    }
    redirectingToLogin = true;
    const hadToken = !!getAdminToken();
    clearStoredLogin();
    setLoginNotice(notice || (hadToken ? 'Your session has expired. Please log in again.' : 'Please log in.'));
    window.location.assign('/authentication/login2');
}

function isCompanyAdmin() {
    try {
        return localStorage.getItem('admin_level') === 'c';
    } catch (e) {
        return false;
    }
}

// The API answers HTTP 200 with the real status in the body's "code"; also handle a real HTTP 401.
// Code 423 means the login or its company is suspended. A suspended company's own admin (level c) stays
// signed in and is kept on the Account page's Billing tab with a banner (the allowed routes also answer
// "suspended": true);
// a district manager or reseller is signed out like an expired session.
api.interceptors.response.use(
    (response) => {
        const data = response && response.data;
        if (data && data.code == 401) {
            handleSessionExpired(response.config);
        } else if (data && data.code == 423) {
            const error = (data.message && data.message.error) || SUSPENDED_NOTICE;
            if (isCompanyAdmin()) {
                setCompanySuspended(data.suspended_message || error);
            } else {
                handleSessionExpired(response.config, error);
            }
        } else if (data && data.suspended === true && isCompanyAdmin() && getAdminToken()) {
            setCompanySuspended(data.suspended_message || SUSPENDED_NOTICE);
        }
        return response;
    },
    (error) => {
        if (error && error.response && error.response.status === 401) {
            handleSessionExpired(error.config);
        }
        return Promise.reject(error);
    }
);

export async function sendRequest(endpoint, method, auth, postObj=null, params=null){
    const jsonParam = 'application/json';
    const formdataParam = 'multipart/form-data';
    // Built synchronously here, so clearing storage straight after a call (logout) cannot drop the header
    const config = withAdminToken(endpoint);

    if(auth) {
      let authToken = localStorage.getItem('access_token');
    //   api.defaults.headers.common['Authorization'] = "Token "+authToken;
      api.defaults.headers.common['token'] = authToken;
    // api.defaults.headers.common['credentials'] = "include";
    } else {
      delete api.defaults.headers.common['Authorization'];
    }

    if (params) {
      api.defaults.params = params
    } else {
      api.defaults.params = null
    }

    switch(method){
        case requestMethod.GET:
            api.defaults.headers['Accept'] = jsonParam;
            api.defaults.headers['Content-Type'] = jsonParam;
            return api.get(endpoint, config);
        case requestMethod.POST:
            api.defaults.headers['Accept'] = jsonParam;
            api.defaults.headers['Content-Type'] = jsonParam;
            return api.post(endpoint, postObj, config);
        case requestMethod.PUT:
            api.defaults.headers['Accept'] = jsonParam;
            api.defaults.headers['Content-Type'] = jsonParam;
            return api.put(endpoint, postObj, config);
        case requestMethod.FILE:
            api.defaults.headers['Accept'] = formdataParam;
            api.defaults.headers['Content-Type'] = formdataParam;
            let formData = new FormData();
            formData.append("file", postObj.file);
            return api.post(endpoint, formData, config);
        case requestMethod.DELETE:
            return api.delete(endpoint, config);
        default:
            break;
    }
}
